import React, { Fragment, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import styles from './SignUp.module.css'

import EntryForm, { EntryFormContainer, EntryFormError } from 'Layout/EntryForm'
import FormControl from 'Design/components/FormControl'
import Label from 'Design/components/Label'
import Input from 'Design/components/Input'
import FormErrorText from 'Design/components/FormErrorText'
import Button from 'Design/components/Button'

import signupFormValidationSchema from './signup-form-validation-schema'
import { useSignupMutation, useCheckEmailSignupMutation } from './signup.service'

import { loadRecaptcha } from '../../../utils/recaptcha'

const FormLabel = (props) => {
  return <Label className={styles.FormLabel} {...props} />
}

const { RECAPTCHA_SITE_KEY } = process.env.CONFIG

const SignUp = () => {
  const [validationSchema, setValidationSchema] = useState(signupFormValidationSchema)

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    setError,
    clearErrors
  } = useForm({
    defaultValues: {
      name: '',
      email: '',
      password: '',
      rePassword: '',
      companyName: '',
      activationCode: '',
      apiError: '' // not a form field
    },
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema)
  })

  const [signup, signupResult] = useSignupMutation()
  const [checkEmailSignup] = useCheckEmailSignupMutation()
  const navigate = useNavigate()

  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    loadRecaptcha().then(() => {
      window.grecaptcha.render('signup-recaptcha', {
        callback: (token) => {
          handleSubmit(submitForm)(token)
        },
        'expired-callback': () => {
          processError({
            type: 'RECAPTCHA_ERROR',
            message: 'ReCaptcha expired. Please try again.'
          })
        },
        'error-callback': () => {
          processError({
            type: 'RECAPTCHA_ERROR',
            message: 'ReCaptcha verification failed. Please reload the page.'
          })
        }
      })
    })
  }, [])

  const processError = (err = {}) => {
    switch (err.type) {
      case 'INVALID_EMAIL':
        setError('email', err, { shouldFocus: true })
        break

      case 'MIN_COMPANY_LENGTH_REQUIRED':
        setError('companyName', err, { shouldFocus: true })
        break

      case 'INVALID_ACTIVATION_CODE':
      case 'ACTIVATION_CODE_EXPIRED':
        setError('activationCode', err, { shouldFocus: true })
        break

      default:
        setError('apiError', err)
    }
  }

  const onFormSubmit = () => {
    if (window.grecaptcha) {
      window.grecaptcha.execute()
    } else {
      processError({
        type: 'RECAPTCHA_ERROR',
        message: 'Unable to load Google reCAPTCHA. Try reloading the page.'
      })
    }
  }

  const submitForm = async (data, recaptchaToken) => {
    clearErrors('apiError')

    if (!recaptchaToken) {
      processError({
        type: 'CAPTCHA_REQUIRED',
        message: 'Captcha required'
      })
      return
    }

    const payload = {
      name: data.name,
      email: data.email,
      password: data?.password || null,
      company_name: data.companyName,
      activation_code: data.activationCode,
      recaptcha_token: recaptchaToken
    }

    try {
      await signup(payload).unwrap()
      navigate('./success', { state: { email: data.email } })
    } catch (e) {
      console.error('Unable to signup', e)
      window.grecaptcha?.reset()
      const error = e.data || {}
      switch (error.type) {
        case 'VALIDATION_ERR':
          processError(error.errors[0])
          break

        default:
          processError(error)
      }
    }
  }

  const isSigningIn = signupResult.isLoading || isSubmitting

  const handleEmailChange = async (event) => {
    const emailValue = event.target.value

    if (!emailValue) {
      return
    }

    await checkEmailSignup({ email: emailValue })
      .unwrap()
      .then(({ is_signed_up }) => {
        if (is_signed_up) {
          const updatedValidationSchema = validationSchema.clone()
          setValidationSchema(
            updatedValidationSchema.shape({
              password: yup.string().optional(),
              rePassword: yup.string().optional()
            })
          )
          setShowPassword(false)
        } else {
          setShowPassword(true)
          setValidationSchema(signupFormValidationSchema)
        }
      })
      .catch((err) => {
        console.error('Unable to check email signup', err)
      })
  }

  return (
    <EntryForm className={styles.Container}>
      <EntryFormContainer className={styles.FormContainer}>
        {errors.apiError && <EntryFormError>{errors.apiError.message}</EntryFormError>}

        <form className={styles.SignupForm} onSubmit={handleSubmit(onFormSubmit)}>
          <FormControl isInvalid={errors.name}>
            <FormLabel htmlFor='name'>Full Name</FormLabel>
            <Input type='text' id='name' {...register('name')} placeholder='Enter your full name' />
            {errors.name && <FormErrorText>{errors.name.message}</FormErrorText>}
          </FormControl>

          <FormControl isInvalid={errors.email}>
            <FormLabel htmlFor='email'>Work Email</FormLabel>
            <Input
              type='text'
              id='email'
              {...register('email')}
              placeholder='Enter your work email'
              onBlur={handleEmailChange}
            />
            {errors.email && <FormErrorText>{errors.email.message}</FormErrorText>}
          </FormControl>

          {showPassword && (
            <Fragment>
              <FormControl isInvalid={errors.password}>
                <FormLabel htmlFor='password'>Password</FormLabel>
                <Input type='password' id='password' {...register('password')} placeholder='Enter your password' />
                {errors.password && <FormErrorText>{errors.password.message}</FormErrorText>}
              </FormControl>

              <FormControl isInvalid={errors.rePassword}>
                <FormLabel htmlFor='rePassword'>Confirm Password</FormLabel>
                <Input
                  type='password'
                  id='rePassword'
                  {...register('rePassword')}
                  placeholder='Re enter your password'
                />
                {errors.rePassword && <FormErrorText>{errors.rePassword.message}</FormErrorText>}
              </FormControl>
            </Fragment>
          )}

          <FormControl isInvalid={errors.companyName}>
            <FormLabel htmlFor='companyName'>Company Name</FormLabel>
            <Input type='text' id='companyName' {...register('companyName')} placeholder='Enter your company name' />
            {errors.companyName && <FormErrorText>{errors.companyName.message}</FormErrorText>}
          </FormControl>

          <FormControl isInvalid={errors.activationCode}>
            <FormLabel htmlFor='activationCode'>Activation Code</FormLabel>
            <Input
              type='text'
              id='activationCode'
              {...register('activationCode')}
              placeholder='Enter your activation code'
            />
            {errors.activationCode && <FormErrorText>{errors.activationCode.message}</FormErrorText>}
          </FormControl>

          <div id='signup-recaptcha' className='g-recaptcha' data-sitekey={RECAPTCHA_SITE_KEY} data-size='invisible' />

          <Button variant='primary' type='submit' disabled={isSigningIn}>
            {isSigningIn ? 'Creating account...' : 'Create my account'}
          </Button>
          <div className={styles.Footer}>
            <p className={styles.Terms}>
              By clicking '<span className='text-uppercase'>Create my account</span>' button you agree <br /> to our{' '}
              <a href='https://www.happyfox.com/terms-services/' target='_blank' rel='noreferrer'>
                Terms of Service
              </a>{' '}
              and{' '}
              <a target='_blank' href='https://www.happyfox.com/privacy-policy/' rel='noreferrer'>
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </form>
      </EntryFormContainer>
      <p className={styles.Copyright}>Copyright © 2020, HappyFox Inc. All Rights Reserved.</p>
    </EntryForm>
  )
}

export default SignUp
